import {Component, inject, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';

import {ActivatedRoute} from "@angular/router";
import {CustomerBillingAccountService} from "../../../../service/api/customer-billing-account.service";
import {CustomerBillingAccountDto} from "../../../../model/dto/entity/customer-billing-account.dto";
import {CustomerBillingAccountBankDto} from "../../../../model/dto/entity/customer-billing-account-bank.dto";
import {
    BM_ACCOUNT_BANK_SLICES_QUERY,
    bmBankSliceEndDateForDisplay,
    bmBankSlicesQuery,
    bmFindCurrentBankSlice,
    bmIsoDateOnly,
    bmReadBankSlices,
    bmReadCurrentBankAccountFromComplexNode,
    bmTodayIsoDate,
    bmWrapCurrentBankAccountAsSlice
} from "../../../../logic/current-bank-account/bm-current-bank-account-ql";
import type {BmBankSliceRow} from "../../../../logic/current-bank-account/bm-bank-slice-row";
import {
    BM_PAYMENT_METHOD_LABELS,
    isBmDirectDebitPaymentMethod,
    resolveBmPaymentEnumLabel
} from "../../../../logic/payment-enum-labels/bm-payment-enum-labels";
// TM-1000002026: the „ab"/„bis" tags are part of the row model of the full view now (bm-contract-link-rows);
// bmSortContractLinks puts the slices of one contract next to each other
import {
    bmCountEndedContractLinks,
    bmFilterVisibleContractLinks,
    bmSortContractLinks
} from "../../../../logic/contract-links/bm-contract-link-visibility";
import {
    BM_RUNNING_DUNNINGS_PIPE_SETTINGS,
    BM_RUNNING_DUNNINGS_QUERY,
    bmFoldRunningDunnings,
    bmRunningDunningsQuery
} from "../../../../logic/running-dunning/bm-running-dunning-ql";
import type {BmRunningDunning} from "../../../../logic/running-dunning/bm-running-dunning";
import {
    BM_OPEN_CW_CASE_LINES_PIPE_SETTINGS,
    BM_OPEN_CW_CASE_LINES_QUERY,
    bmOpenCwCaseLinesQuery,
    bmReadOpenCwCaseLines
} from "../../../../logic/open-cw-case-lines/bm-open-cw-case-lines-ql";
import type {BmOpenCwCaseLine} from "../../../../logic/open-cw-case-lines/bm-open-cw-case-line";
import {QlService} from "../../../../../../core/shared/ql/service/ql.service";
import {QlQueryResult} from "../../../../../../core/shared/ql/builder/ql-query-builder";
import {ContractPaymentAccountDto} from "../../../../model/dto/entity/contract-payment-account.dto";
import {ObjectBaseModeComponent} from "../../../../../../core/shared/object/mvs-object-base/object-base-mode.component";
import {PaymentMethod} from "../../../../enum/payment-method.enum";
import {FilterCriteria} from "../../../../../../core/shared/filter/api/filter.criteria";
import {Sorting} from "../../../../../../core/shared/misc/sorting";
import {ObjectRequestList} from "../../../../../../core/shared/dto/object/request/object-request-list";
import {ObjectRequestComplex} from "../../../../../../core/shared/dto/object/request/object-request-complex";
import {ObjectRequestComplexNode} from "../../../../../../core/shared/dto/object/request/object-request-complex-node";
import {ObjectRequestRelation} from "../../../../../../core/shared/dto/object/request/object-request-relation";
import {
    ObjectRequestComplexRelationBindingEnum
} from "../../../../../../core/shared/dto/object/request/object-request-complex-relation-binding.enum";
import {DtoList} from "../../../../../../core/shared/dto/dto.list";
import {ObjectIdentifier} from "../../../../../../core/shared/basic/object-identifier";
import {MenuItem} from "primeng/api";
import {MvsObjectService} from "../../../../../../core/shared/service/object/mvs-object.service";
import {Subscription} from "rxjs";
import {BmBillingAccountSepaStateDto} from "../../../../model/dto/api/bm-billing-account-sepa-state.dto";

@Component({
    selector: 'bm-customer-account-object-component-base',
    template: '',
    standalone: false
})
export class BmCustomerAccountObjectBaseComponent extends ObjectBaseModeComponent implements OnInit, OnChanges, OnDestroy {

    customerBillingAccountDto: CustomerBillingAccountDto;

    // Toggle for showing old contracts
    includeOldContracts: boolean = false;

    /**
     * The contract payment accounts the Verträge tab renders — a STABLE array reference, rebuilt
     * only by {@link recomputeVisibleContractPaymentAccounts}.
     *
     * Deliberately a field and not a getter. A getter would call Array#filter on every read, and
     * `filter` returns a NEW array instance each time. That fresh reference is bound to the
     * p-table's [value] and to two .length bindings in the tab LIST (evaluated even while the tab
     * is closed), so Angular's Object.is comparison in bindingUpdated would mark the input dirty on
     * EVERY change-detection tick — re-entering PrimeNG Table.onChanges, recomputing totalRecords
     * and re-emitting tableService.onValueChange to every row subscriber, for as long as the view
     * is open. Nothing throws and no build catches it: in dev mode Angular's checkNoChanges deep-
     * compares iterables, so the churn is silent. Do not "simplify" this back into a getter.
     */
    visibleContractPaymentAccounts: ContractPaymentAccountDto[] = [];

    /**
     * How many link rows the switch „Abgelaufene Verträge anzeigen" is currently HIDING - the hint
     * „({n} beendete ausgeblendet)" next to it. Exactly the ENDED rows, never a slice that has not
     * started yet (D-R11-1); 0 while the switch is on. Rebuilt together with
     * {@link visibleContractPaymentAccounts}, by {@link recomputeVisibleContractPaymentAccounts}.
     */
    hiddenEndedContractCount: number = 0;

    // Loading state
    loading: boolean = false;

    // Active tab index for History tabs
    activeTabIndex: number = 0;

    // Dunning sub-navigation state
    showDunningObject: boolean = false;
    dunningObjectIdentifier: ObjectIdentifier | null = null;
    dunningBreadcrumb: MenuItem[] = [];

    contractPriceTotalCurrent: number | null = null;
    contractPriceTotalFuture: number | null = null;
    contractPriceTotalsLoading: boolean = false;

    /**
     * SEPA failure state of the displayed account: the consecutive failed collection attempts inside
     * the observation window, date and configured return reason of the most recent one, plus the
     * account's collection block and its free-text suspension note.
     *
     * Not part of `customerBillingAccountDto`: the failure values are derived by the backend on every
     * read and are not entity attributes, so neither the entity DTO nor a QL query can carry them.
     * Source: {@link CustomerBillingAccountService.getSepaFailureState}.
     *
     * null = no answer for the displayed account (yet). Whether a read is running or has failed is
     * told by `sepaFailureStateLoading` and `sepaFailureStateError` — null alone never means
     * "no failure".
     */
    sepaFailureState: BmBillingAccountSepaStateDto | null = null;

    /** True while the SEPA failure state read for the displayed account is in flight. */
    sepaFailureStateLoading: boolean = false;

    /**
     * True when the last SEPA failure state read for the displayed account FAILED. Set only by a real
     * failure, never inferred from a null state. The views must then say that the state is unknown
     * instead of rendering the account as free of failures: the global error toast is suppressed for
     * this read, so this flag is the only place the failure surfaces.
     */
    sepaFailureStateError: boolean = false;

    /**
     * ALL bank slices (bm.CustomerBillingAccountBank) of the displayed account, oldest first, classified
     * for DISPLAY against today (E-2) - read by QL (`bmBankSlicesQuery`) in parallel with the account
     * itself, like the SEPA failure state. The header derives from them (round 11, WP-B/B3, D3):
     * {@link currentBankSlice} lends its `id / startDate / endDate` to `currentBankAccountDto`, so the
     * header and the basic-details card print the SLICE's validity („Bankverbindung gültig ab", E-7)
     * and never the bank account's own dates again; {@link plannedBankSlice} is the „ab {Datum}: {Bank}"
     * line. Whether a read is running or has failed is told by the two flags, never by an empty list.
     */
    bankSlices: BmBankSliceRow[] = [];
    currentBankSlice: BmBankSliceRow | null = null;
    plannedBankSlice: BmBankSliceRow | null = null;
    bankSlicesLoading: boolean = false;
    bankSlicesError: boolean = false;

    /**
     * The dunning that is RUNNING on the displayed account (`bd.Dunning`, status ACTIVE or SUSPENDED),
     * null when there is none. Derived by QL in parallel with the account read
     * (`bmRunningDunningsQuery`) - the dunning FK of `bm.CustomerBillingAccount` (column active_dunning_id) is never read: it has no
     * writer, so it is empty for system-started dunnings and stale for ended ones. The views bind this
     * field; {@link runningDunningLoading} is true while the read is in flight.
     */
    runningDunning: BmRunningDunning | null = null;
    runningDunningLoading: boolean = false;

    /**
     * The OPEN applications of the displayed account - `cw.CwCaseProduct` lines whose Vorgang pays
     * through it, „Einreichbar" or „Wartet auf Policierung", not converted into a contract yet (rule:
     * bm-open-cw-case-lines-ql.ts), newest Vorgang first. Read by QL in parallel with the account, like
     * the bank slices, and ONLY by a view that renders them ({@link loadsOpenCwCaseLines}).
     *
     * A STABLE array reference, replaced only when an answer lands - bound to a p-table's [value] and
     * counted into the Verträge tab badge (see {@link visibleContractPaymentAccounts} for why that
     * matters). Whether a read is running or has failed is told by the two flags, never by an empty list.
     */
    openCwCaseLines: BmOpenCwCaseLine[] = [];
    openCwCaseLinesLoading: boolean = false;

    /**
     * True when the last read for the displayed account FAILED - an error or a missing permission for
     * cw. Set only by a real failure. The global error toast is suppressed for this read, so this flag
     * is the only place the failure surfaces; it never touches the contract table.
     */
    openCwCaseLinesError: boolean = false;

    /** The account {@link openCwCaseLines} belong to - a refresh of the same account keeps them until the answer. */
    private openCwCaseLinesAccountId: number | null = null;

    /**
     * What the header prints after „gültig ab" / „bis" - the SLICE's 'yyyy-MM-dd' start and its end
     * (null while unknown, and null for the open-ended slice: no „bis 31.12.9999", E-7). Fields, not
     * getters (compliance review, round 11): normalised ONCE in {@link attachCurrentBankSliceToHeader}
     * after either parallel read lands; the full view's template binds the two fields directly.
     */
    headerBankValidFrom: string | null = null;
    headerBankValidTo: string | null = null;

    private objectChangeSubscription: Subscription;
    private sepaFailureStateSubscription: Subscription;
    private bankSlicesSubscription: Subscription;
    private runningDunningSubscription: Subscription;
    private openCwCaseLinesSubscription: Subscription;

    /**
     * `inject()` in a field initialiser rather than a constructor parameter ON PURPOSE: both view
     * subclasses (full, side) call `super(route, service, objectService)`, and the side view is not
     * touched by round 11. Same technique the framework's ObjectBaseModeComponent uses for its own
     * collaborators; the spec provides the service through TestBed.runInInjectionContext.
     */
    private readonly qlService: QlService = inject(QlService);

    constructor(
        protected route: ActivatedRoute,
        protected customerBillingAccountService: CustomerBillingAccountService,
        protected objectService: MvsObjectService
    ) {
        super(route);
    }

    ngOnInit() {
        super.ngOnInit();
        this.getCustomerBillingAccount();
        this.subscribeToObjectChanges();
    }

    getCustomerBillingAccount() {
        this.loading = true;

        // Fired in PARALLEL with the account read, not from onCustomerBillingAccountLoaded(): the SEPA
        // failure state needs nothing but the account id, so it must not wait for the (much larger)
        // object-request-complex read of the account itself. Every path that refreshes the account —
        // ngOnInit, ngOnChanges on objectIdentifier and the object-change subscription — runs through
        // here, so the state is refreshed with the account. The old-contracts toggle is NOT such a
        // path: it only re-filters the rows already loaded (recomputeVisibleContractPaymentAccounts).
        this.loadSepaFailureState();

        // The bank slices of the account, for the same reason and in the same way (round 11, B3).
        this.loadBankSlices();

        // And the running dunning, derived from bd.Dunning (not from the dead active_dunning_id FK).
        this.loadRunningDunning();

        // And the open applications (cw lines without a contract yet) - only for a view that shows them.
        if (this.loadsOpenCwCaseLines()) {
            this.loadOpenCwCaseLines();
        }

        const objectRequestList = this.createBillingAccountObjectRequest(this.objectIdentifier.objectId);

        this.customerBillingAccountService.list(objectRequestList).subscribe((response: DtoList<CustomerBillingAccountDto>) => {
            if (response.entries?.length > 0) {
                this.customerBillingAccountDto = response.entries[0];
                this.reshapeCurrentBankAccount(this.customerBillingAccountDto);
                // Whichever of the two parallel reads lands last completes the header - see the method.
                this.attachCurrentBankSliceToHeader();
                this.loadContractPriceTotals();
            }
            // On the single completion path ON PURPOSE, outside the if: the rendered list is always
            // rebuilt from whatever this load produced, so it can never drift from the DTO.
            // NOTE what this does NOT do: on an empty response the DTO above is left untouched, so
            // the rows are rebuilt from the PREVIOUS account. Clearing them would mean clearing the
            // DTO itself, i.e. changing the component's not-found behaviour — deliberately out of
            // scope here, and the whole view (header, KPI tiles, tabs) is stale in that case too.
            this.recomputeVisibleContractPaymentAccounts();
            this.initialized = true;
            this.loading = false;
            // TM-1000001932: Daten-getriebener Hook — wird nach Abschluss des
            // Konto-Loads aufgerufen (Subscribe-Completion), sobald
            // customerBillingAccountDto verfügbar ist (die laufende Mahnung kommt getrennt, siehe
            // onRunningDunningLoaded()).
            // Subklassen hängen hier abgeleitete Ladevorgänge ein, statt eine
            // Methode in der Template-Interpolation aufzurufen (Anti-Pattern).
            this.onCustomerBillingAccountLoaded();
        });
    }

    /**
     * TM-1000001932: Erweiterungspunkt nach abgeschlossenem Laden des
     * Abrechnungskontos. Standard: keine Aktion. Subklassen überschreiben dies,
     * um abhängige Daten zu laden (z. B. die Storno-Vorausbuchung der aktiven
     * Mahnung), gespeist aus dem Datenfluss statt aus dem Template.
     */
    protected onCustomerBillingAccountLoaded(): void {
        // No-op in base; overridden by subclasses.
    }

    /**
     * Erweiterungspunkt nach abgeschlossenem Laden der laufenden Mahnung
     * ({@link runningDunning} ist gesetzt oder null). Standard: keine Aktion.
     */
    protected onRunningDunningLoaded(): void {
        // No-op in base; overridden by subclasses.
    }

    /**
     * Reads the running dunning of the displayed account from `bd.Dunning` (status ACTIVE or
     * SUSPENDED, see bm-running-dunning-ql.ts) into {@link runningDunning}. An answer that arrives
     * after the displayed account changed is discarded, like the SEPA failure state read.
     */
    protected loadRunningDunning(): void {

        const accountId = this.objectIdentifier?.objectId;

        this.runningDunningSubscription?.unsubscribe();

        if (!accountId) {
            this.applyRunningDunningAnswer(accountId, null);
            return;
        }

        this.runningDunningLoading = true;
        this.runningDunningSubscription = this.qlService.builder()
            .query(bmRunningDunningsQuery([accountId]))
            .pipe({
                name: BM_RUNNING_DUNNINGS_QUERY,
                simpleQuery: BM_RUNNING_DUNNINGS_QUERY,
                settings: BM_RUNNING_DUNNINGS_PIPE_SETTINGS
            })
            .execute()
            .subscribe({
                next: (result: QlQueryResult) => this.applyRunningDunningAnswer(
                    accountId, bmFoldRunningDunnings(result.get(BM_RUNNING_DUNNINGS_QUERY)).get(Number(accountId)) ?? null),
                error: () => this.applyRunningDunningAnswer(accountId, null)
            });
    }

    private applyRunningDunningAnswer(requestedAccountId: number, dunning: BmRunningDunning | null): void {
        const displayedAccountId = this.objectIdentifier?.objectId;
        if (requestedAccountId
            && !BmCustomerAccountObjectBaseComponent.isSameAccountId(displayedAccountId, requestedAccountId)) {
            return;
        }
        this.runningDunningLoading = false;
        this.runningDunning = dunning;
        this.onRunningDunningLoaded();
    }

    /**
     * Whether this view reads {@link openCwCaseLines} on every account (re)load. False here: the side
     * view shares this base and shows no open applications, so it must not pay for - or fail on - the
     * cw read. The full view, which renders them in the Verträge tab, answers true.
     */
    protected loadsOpenCwCaseLines(): boolean {
        return false;
    }

    /**
     * Reads the open applications of the displayed account (`bmOpenCwCaseLinesQuery`) into
     * {@link openCwCaseLines} and keeps `openCwCaseLinesLoading` / `openCwCaseLinesError` in step.
     *
     * Lines of ANOTHER account are dropped at once; a refresh of the SAME account keeps its lines until
     * the new answer lands, so the tab badge does not flicker. `showErrors(false)`: a failure (also a
     * missing cw permission) is told by the section itself, not by a global toast over the account. An
     * answer that arrives after the displayed account changed is discarded, like the other parallel reads.
     */
    protected loadOpenCwCaseLines(): void {

        const accountId = this.objectIdentifier?.objectId;

        this.openCwCaseLinesSubscription?.unsubscribe();
        this.openCwCaseLinesError = false;

        if (!BmCustomerAccountObjectBaseComponent.isSameAccountId(this.openCwCaseLinesAccountId, accountId)) {
            this.openCwCaseLines = [];
            this.openCwCaseLinesAccountId = null;
        }

        if (!accountId) {
            this.openCwCaseLinesLoading = false;
            return;
        }

        this.openCwCaseLinesLoading = true;
        this.openCwCaseLinesSubscription = this.qlService.builder()
            .query(bmOpenCwCaseLinesQuery(accountId))
            .pipe({
                name: BM_OPEN_CW_CASE_LINES_QUERY,
                simpleQuery: BM_OPEN_CW_CASE_LINES_QUERY,
                settings: BM_OPEN_CW_CASE_LINES_PIPE_SETTINGS
            })
            .showErrors(false)
            .execute()
            .subscribe({
                next: (result: QlQueryResult) => {
                    const read = bmReadOpenCwCaseLines(result.getPipeResult(BM_OPEN_CW_CASE_LINES_QUERY));
                    this.applyOpenCwCaseLinesAnswer(accountId, read.lines, read.failed);
                },
                error: () => this.applyOpenCwCaseLinesAnswer(accountId, [], true)
            });
    }

    /**
     * Applies the outcome of one open-application read - only for the account that is still displayed.
     *
     * The request the loading flag describes is over, whatever its answer, so the flag drops in every
     * case: a newer read would have unsubscribed this one (loadOpenCwCaseLines), so an answer that still
     * arrives for another account means no read is running - keeping the flag up would pin the hint. Such
     * an answer is not shown, and lines of an account other than the displayed one are dropped with it.
     */
    private applyOpenCwCaseLinesAnswer(requestedAccountId: number, lines: BmOpenCwCaseLine[], failed: boolean): void {

        this.openCwCaseLinesLoading = false;

        const displayedAccountId = this.objectIdentifier?.objectId;
        if (!BmCustomerAccountObjectBaseComponent.isSameAccountId(displayedAccountId, requestedAccountId)) {
            const linesAccountId = this.openCwCaseLinesAccountId;
            if (!BmCustomerAccountObjectBaseComponent.isSameAccountId(linesAccountId, displayedAccountId)) {
                this.openCwCaseLines = [];
                this.openCwCaseLinesAccountId = null;
            }
            return;
        }

        this.openCwCaseLinesError = failed;
        this.openCwCaseLines = failed ? [] : lines;
        this.openCwCaseLinesAccountId = requestedAccountId;
    }

    /**
     * Loads the SEPA failure state of the displayed account into `sepaFailureState` and keeps
     * `sepaFailureStateLoading` / `sepaFailureStateError` in step with the request.
     *
     * A failed read sets `sepaFailureStateError` and clears the state; the account view itself keeps
     * rendering. An answer (or failure) that arrives after the displayed account changed is discarded,
     * but it still ends the loading state it belongs to.
     */
    protected loadSepaFailureState(): void {

        const accountId = this.objectIdentifier?.objectId;

        this.sepaFailureStateSubscription?.unsubscribe();
        this.sepaFailureStateError = false;

        // Drop a state that belongs to a DIFFERENT account immediately, so a slow read can never leave
        // the banner of the account we just navigated away from standing. A refresh of the SAME account
        // (balance refresh, object-change event, old-contracts toggle) keeps the current values until
        // the new ones arrive, so the banner does not flicker on every refresh.
        this.dropSepaFailureStateOfOtherAccount(accountId);

        if (!accountId) {
            this.sepaFailureState = null;
            this.sepaFailureStateLoading = false;
            return;
        }

        this.sepaFailureStateLoading = true;
        this.sepaFailureStateSubscription = this.customerBillingAccountService
            .getSepaFailureState(accountId)
            .subscribe({
                next: (state: BmBillingAccountSepaStateDto) =>
                    this.applySepaFailureStateAnswer(accountId, state, false),
                error: () =>
                    this.applySepaFailureStateAnswer(accountId, null, true)
            });
    }

    /**
     * Applies the outcome of one SEPA failure state read.
     *
     * @param requestedAccountId the account id the finished request was sent for
     * @param state the answer of a successful read; null when the read failed
     * @param failed true exactly when the read ended in the service's `error` notification
     */
    private applySepaFailureStateAnswer(requestedAccountId: number,
                                        state: BmBillingAccountSepaStateDto | null,
                                        failed: boolean): void {

        // The request this flag describes is over, whatever its answer. Leaving the flag set for a
        // discarded answer would keep the loading placeholder up with no request left to clear it.
        this.sepaFailureStateLoading = false;

        const displayedAccountId = this.objectIdentifier?.objectId;

        if (!BmCustomerAccountObjectBaseComponent.isSameAccountId(displayedAccountId, requestedAccountId)) {
            // The displayed account changed while the request was in flight: the answer (or failure)
            // belongs to another account and must neither be shown nor flag the displayed one.
            this.dropSepaFailureStateOfOtherAccount(displayedAccountId);
            return;
        }

        this.sepaFailureState = failed ? null : state;
        this.sepaFailureStateError = failed;
    }

    /** Clears `sepaFailureState` when it belongs to an account other than `accountId`. */
    private dropSepaFailureStateOfOtherAccount(accountId: number | null | undefined): void {
        if (this.sepaFailureState
            && !BmCustomerAccountObjectBaseComponent.isSameAccountId(this.sepaFailureState.accountId, accountId)) {
            this.sepaFailureState = null;
        }
    }

    /**
     * Whether two account ids denote the same account. Every SEPA-state id comparison goes through
     * here: ids are compared as numbers, because backend Longs can arrive as strings, and a missing id
     * matches no account.
     */
    private static isSameAccountId(first: number | null | undefined, second: number | null | undefined): boolean {
        return first != null && second != null && Number(first) === Number(second);
    }

    /**
     * Creates an ObjectRequestList with ObjectRequestComplex for fetching comprehensive billing account data.
     *
     * ALL contract payment accounts are loaded, unconditionally; the view decides visibility.
     * The link rows' endDate mechanics matter here: `bm.ContractPaymentAccount implements
     * EntityStartDateWithAutomaticHighDate` and its @EntityFieldBusinessKey is the CONTRACT ALONE
     * (the billing account is not part of the key), so `EntityPeriodService` maintains ONE
     * contiguous timeline per contract — each row's endDate is derived as the next row's startDate
     * minus one day, and only the LAST row is high-dated to 9999-12-31. A contract that moved
     * accounts therefore leaves a superseded link row here with a REAL PAST endDate. A posted end
     * date is still discarded in favour of the derived one (measured 16 Sep 2026 on dev/tenantA:
     * 21 rows POSTed with past end dates all came back 9999-12-31, HTTP 200, no message).
     *
     * The former server-side `endDate >= TODAY` relation filter therefore DID do something: it
     * dropped exactly those superseded link rows (and was a no-op only for single-row contracts,
     * the common case). What it never implemented was the toggle's label — "abgelaufene VERTRÄGE"
     * — which is a different question from "is this link row still this account's current
     * assignment". Conflating those two questions produced the double-count bug this file carried;
     * both are now answered explicitly, per row: for the rendered rows by the pure rule in
     * bm-contract-link-visibility (`bmClassifyContractLink`, see
     * {@link recomputeVisibleContractPaymentAccounts}), for the premium totals by
     * {@link loadContractPriceTotals}.
     *
     * @param id The billing account ID
     */
    createBillingAccountObjectRequest(id: number): ObjectRequestList {
        const filterCriteria = FilterCriteria.create('id', FilterCriteria.cOperatorEqual, id);
        const objectRequestList = ObjectRequestList.createBasic(false, [filterCriteria], []);

        // Build ObjectRequestComplex with all relations
        objectRequestList.objectRequestComplex = ObjectRequestComplex.build(
            true,
            false,
            // ManyToOne: Customer
            ObjectRequestComplexNode.createSimpleAttributeNode('customer'),
            // ManyToOne: Billing Run Type — carries frequency/paymentTiming/paymentDayOfMonth
            // (TM-1000001979 follow-up: was missing, leaving billingRunTypeDto always undefined
            // and the frequency/payment-day fields silently blank in the basic-details card).
            ObjectRequestComplexNode.createSimpleAttributeNode('billingRunType'),
            // CURRENT SEPA bank account (H1) - the mandate-agnostic MetaExtend join
            // `+currentBankAccount` (bm/meta/MetaExtendCustomerBillingAccountService), resolved
            // server side as the bm.CustomerBillingAccountBank slice with the highest
            // startDate <= TODAY. Replaces the former `createLatest('bm.CustomerBillingAccountBank',
            // ...)` binding, which picked by createdDate/id and could surface a FUTURE slice already
            // queued for a later bank change - exactly the bug this join exists to avoid. The join
            // returns `cr.CustomerBankAccount` directly (no more SLICE hop in between); the response
            // is reshaped back onto the two-level `currentBankAccountDto.customerBankAccountDto`
            // shape the view (bm-customer-account-basic-details.component.html) already reads - see
            // {@link getCustomerBillingAccount}.
            ObjectRequestComplexNode.createRelationNode(
                'currentBankAccountDto',
                ObjectRequestRelation.createJoin('+currentBankAccount')
            ),
            // The last SEPA return reason is not an entity attribute; it arrives with the SEPA failure
            // state (loadSepaFailureState), not through this request.
            // NO node for the dunning FK (active_dunning_id): it has no writer and is never read. The running dunning
            // (and its Mahnstufe name via activeLevel -> dunningTypeLevel) comes from
            // {@link loadRunningDunning}, a QL read over bd.Dunning.
            // OneToOne (Latest): Balance
            ObjectRequestComplexNode.createRelationNode(
                'customerBillingAccountBalanceDto',
                ObjectRequestRelation.createLatest('bm.CustomerBillingAccountBalance', 'customerBillingAccount')
            ),
            // OneToMany: Contract Payment Accounts — always complete; the view filters.
            ObjectRequestComplexNode.createRelationNode(
                'contractPaymentAccounts',
                ObjectRequestRelation.createList(
                    'bm.ContractPaymentAccount',
                    'customerBillingAccount',
                    [],
                    [new Sorting('startDate', false)],
                    ObjectRequestComplexRelationBindingEnum.ALL
                )
            ).addNodes(
                // Nested ManyToOne: Contract within ContractPaymentAccount
                ObjectRequestComplexNode.createSimpleAttributeNode('contract')
                    .addNodes(
                        ObjectRequestComplexNode.createRelationNode(
                            'contractPrice',
                            ObjectRequestRelation.createLatest('cm.ContractPrice', 'contract')
                        ),
                        // TM-1000002026: partner and contract type as nested nodes - the Verträge table
                        // shows their SHORT names (contractDto.partnerDto / .contractTypeDto .shortName),
                        // the DtoName of the reference is the long name only
                        ObjectRequestComplexNode.createSimpleAttributeNode('partner'),
                        ObjectRequestComplexNode.createSimpleAttributeNode('contractType')
                    )
            ),
            // OneToMany: IBAN History
            ObjectRequestComplexNode.createRelationNode(
                'ibanHistory',
                ObjectRequestRelation.createList(
                    'bm.CustomerIbanHistory',
                    'billingAccount',
                    [],
                    [new Sorting('createdDate', false)],
                    ObjectRequestComplexRelationBindingEnum.ALL
                )
            )
        );

        return objectRequestList;
    }

    /**
     * Reshapes the `+currentBankAccount` join's answer (the `cr.CustomerBankAccount` directly, see
     * {@link createBillingAccountObjectRequest}) back onto the two-level
     * `currentBankAccountDto.customerBankAccountDto` shape the view
     * (bm-customer-account-basic-details.component.html, getHeaderBankImage/getHeaderBankName/
     * hasDeviantAccountHolder/getDeviantAccountHolderName below) already reads - the SLICE entity is
     * no longer a separate hop on the wire, but the DTO field is still typed as the slice
     * (CustomerBillingAccountBankDto) and every existing consumer expects that nesting.
     */
    private reshapeCurrentBankAccount(dto: CustomerBillingAccountDto): void {
        const bankAccount = bmReadCurrentBankAccountFromComplexNode(dto?.currentBankAccountDto);
        if (bankAccount) {
            dto.currentBankAccountDto = bmWrapCurrentBankAccountAsSlice(bankAccount);
        }
    }

    // ========================================================================
    // Bank slices of the account (round 11, WP-B/B3) - the SLICE decides what „gültig" means (D3)
    // ========================================================================

    /**
     * Reads all bank slices of the displayed account (`bmBankSlicesQuery`, oldest first) and keeps
     * `bankSlicesLoading` / `bankSlicesError` in step with the request. An answer that arrives after
     * the displayed account changed is discarded, exactly like the SEPA failure state read.
     */
    protected loadBankSlices(): void {

        const accountId = this.objectIdentifier?.objectId;

        this.bankSlicesSubscription?.unsubscribe();
        this.bankSlicesError = false;

        if (!accountId) {
            this.applyBankSlicesAnswer(accountId, [], false);
            return;
        }

        this.bankSlicesLoading = true;
        this.bankSlicesSubscription = this.qlService.builder()
            .query(bmBankSlicesQuery(accountId))
            .pipe({name: BM_ACCOUNT_BANK_SLICES_QUERY, simpleQuery: BM_ACCOUNT_BANK_SLICES_QUERY})
            .execute()
            .subscribe({
                next: (result: QlQueryResult) => this.applyBankSlicesAnswer(
                    accountId, bmReadBankSlices(result.get(BM_ACCOUNT_BANK_SLICES_QUERY), bmTodayIsoDate()), false),
                error: () => this.applyBankSlicesAnswer(accountId, [], true)
            });
    }

    /**
     * Applies the outcome of one bank-slice read - only for the account that is still displayed -
     * and lends the current slice's identity to the header.
     */
    private applyBankSlicesAnswer(requestedAccountId: number, slices: BmBankSliceRow[], failed: boolean): void {

        this.bankSlicesLoading = false;

        const displayedAccountId = this.objectIdentifier?.objectId;

        if (requestedAccountId
            && !BmCustomerAccountObjectBaseComponent.isSameAccountId(displayedAccountId, requestedAccountId)) {
            return;
        }

        this.bankSlicesError = failed;
        this.bankSlices = slices ?? [];
        this.currentBankSlice = bmFindCurrentBankSlice(this.bankSlices);
        this.plannedBankSlice = BmCustomerAccountObjectBaseComponent.findFirstPlannedBankSlice(this.bankSlices);
        this.attachCurrentBankSliceToHeader();
    }

    /** The EARLIEST planned slice („ab {Datum}: {Bank}") - the reader delivers them oldest first. */
    static findFirstPlannedBankSlice(slices: BmBankSliceRow[] | null | undefined): BmBankSliceRow | null {
        return (slices ?? []).find(slice => slice.state === 'planned') ?? null;
    }

    /**
     * Lends the current slice's `id / startDate / endDate` to the header's `currentBankAccountDto`
     * (the two-level shape every header template already reads), so `currentBankAccountDto.startDate`
     * IS the slice's validity (E-7 / D3) - bank account 1348 has `startDate = null` while its slice
     * runs 1900-01-01..9999-12-31, and the header must say the latter.
     *
     * ONLY when the slice points at the very bank account the ObjectRequestComplex path returned. A
     * mismatch means the QL display fallback (`endDate`-filtered) and the authoritative resolver
     * (`startDate` only) disagree - the signal of a write path that bypassed the period hook
     * documented in bm-current-bank-account-ql.ts - and the header then shows the bank WITHOUT a
     * validity rather than a validity that belongs to another bank. Pure; spec-covered.
     */
    static attachBankSliceIdentity(bank: CustomerBillingAccountBankDto | null | undefined,
                                   slice: BmBankSliceRow | null | undefined): CustomerBillingAccountBankDto | null {

        const bankAccount = bank?.customerBankAccountDto;

        if (!bankAccount) {
            return bank ?? null;
        }

        const matches: boolean = slice != null
            && slice.bankAccountId != null
            && bankAccount.id != null
            && Number(slice.bankAccountId) === Number(bankAccount.id);

        if (!matches) {
            return bmWrapCurrentBankAccountAsSlice(bankAccount);
        }

        return bmWrapCurrentBankAccountAsSlice(bankAccount, {
            id: slice.sliceId,
            startDate: slice.startDate,
            endDate: slice.endDate
        });
    }

    /**
     * Runs after EITHER parallel read lands; the second run completes what the first could not. Also
     * the ONE place the header's validity fields are derived (the wrapped slice carries 'yyyy-MM-dd'
     * strings in the Date-typed DTO fields - see bmWrapCurrentBankAccountAsSlice).
     */
    private attachCurrentBankSliceToHeader(): void {
        if (!this.customerBillingAccountDto) {
            this.headerBankValidFrom = null;
            this.headerBankValidTo = null;
            return;
        }
        const attached: CustomerBillingAccountBankDto | null = BmCustomerAccountObjectBaseComponent
            .attachBankSliceIdentity(this.customerBillingAccountDto.currentBankAccountDto, this.currentBankSlice);
        this.customerBillingAccountDto.currentBankAccountDto = attached;
        this.headerBankValidFrom = bmIsoDateOnly(attached?.startDate as unknown as string);
        this.headerBankValidTo = bmBankSliceEndDateForDisplay(attached?.endDate as unknown as string);
    }

    /**
     * Recomputes {@link visibleContractPaymentAccounts} and {@link hiddenEndedContractCount}: all
     * rows when "Abgelaufene Verträge anzeigen" is on, otherwise the rows running today AND the
     * ones that have not started yet (rendered with an „ab {Datum}" tag); only ENDED rows - the
     * contract has ended, or the link row itself is superseded - are hidden and counted.
     *
     * The rule lives in bm-contract-link-visibility (`bmClassifyContractLink`). Since TM-1000002026
     * every one of its inputs is a column of the Verträge table: „Zugeordnet ab" / „Zugeordnet bis"
     * (the row's OWN period, „unbefristet" for 31.12.9999) and „Vertragsende" (the CONTRACT's end) -
     * so a superseded row of a still-running contract that the switch brings back reads its real end
     * instead of „Laufend"/AKTIV, and additionally carries the „bis {Datum}" tag (row model of the full
     * view, bm-contract-link-rows). Before D-R11-1 this hid every row not running today and called it
     * „beendet" - a future slice written by a merge (REPOINT / END_AND_NEW) or a bank / frequency change
     * vanished behind „(1 beendete ausgeblendet)".
     *
     * TM-1000002026: the rows are ORDERED so the slices of one contract stand next to each other
     * (`bmSortContractLinks`: VSNR, contract, assignment start ascending, id) - the ObjectRequest
     * delivers them by startDate descending over ALL contracts, which tore one contract's two slices
     * apart. Sorted ONCE here, into a new array, so the reference stays stable between recomputes and
     * the DTO's own list is never reordered.
     *
     * Call this at every point that changes either input of that decision — the loaded row set
     * (the subscribe of {@link getCustomerBillingAccount}, including the empty case) or the toggle
     * (the p-toggleSwitch (onChange) hook). It must NOT become a getter again: see the field.
     */
    recomputeVisibleContractPaymentAccounts(): void {
        const all = this.customerBillingAccountDto?.contractPaymentAccounts ?? [];
        const today = new Date();
        // TM-1000002026: slices of one contract next to each other (VSNR, then assignment start)
        this.visibleContractPaymentAccounts = bmSortContractLinks(
            bmFilterVisibleContractLinks(all, this.includeOldContracts, today));
        this.hiddenEndedContractCount = this.includeOldContracts ? 0 : bmCountEndedContractLinks(all, today);
    }

    /**
     * Normalizes a date bound to local midnight, or null when it is absent or unparseable —
     * an unusable bound must read as "no bound", never as "1970".
     */
    private toLocalMidnightTime(value: Date | string | null | undefined): number | null {
        if (!value) {
            return null;
        }
        const date = new Date(value);
        if (isNaN(date.getTime())) {
            return null;
        }
        date.setHours(0, 0, 0, 0);
        return date.getTime();
    }

    /**
     * Refresh balance data.
     */
    onRefreshBalance(): void {
        this.loading = true;
        this.customerBillingAccountService.refreshBalance(this.objectIdentifier.objectId).subscribe({
            next: () => {
                this.getCustomerBillingAccount();
            },
            error: () => {
                this.loading = false;
            }
        });
    }

    /**
     * The German label of the payment method - the backend's own `@UiEnumValueInfo` texts through the
     * ONE FE helper (Q-11j / FD-R11-3), never a switch with literals: until round 11 this view said
     * „Selbstzahler" / „SEPA ausgesetzt" where every endpoint payload says „Selbstüberweisung" /
     * „SEPA-Lastschrift (pausiert)".
     */
    getPaymentMethodLabel(method: PaymentMethod): string {
        return resolveBmPaymentEnumLabel(BM_PAYMENT_METHOD_LABELS, method);
    }

    getPaymentMethodIcon(): string {
        switch (this.customerBillingAccountDto?.paymentMethod) {
            case PaymentMethod.SELF:            return 'pi pi-money-bill';
            case PaymentMethod.SELF_STANDING_ORDER: return 'pi pi-sync';
            case PaymentMethod.SEPA_DIRECT_DEBIT:   return 'pi pi-building';
            case PaymentMethod.SEPA_DIRECT_DEBIT_SUSPENDED: return 'pi pi-building';
            default:                            return 'pi pi-wallet';
        }
    }

    isSepaDirectDebitSuspended(): boolean {
        return this.customerBillingAccountDto?.paymentMethod === PaymentMethod.SEPA_DIRECT_DEBIT_SUSPENDED;
    }

    /**
     * True for a direct-debit account (active OR suspended) - the ONE E-9 predicate „braucht das Konto
     * eine Bankverbindung?" (FD-R11-2), through the shared helper; a missing slice is a defect only then.
     */
    isSepaDirectDebitAccount(): boolean {
        return isBmDirectDebitPaymentMethod(this.customerBillingAccountDto?.paymentMethod);
    }

    /**
     * The BANK's display token for the header avatar - a FontAwesome name OR an image URL, exactly
     * as `cr.CustomerBankAccount.bankImage` stores it, or EMPTY when the account carries no bank
     * account with an image.
     *
     * PRESENCE IS THE ONLY GATE. Whenever the account has a bank account and that bank account has
     * an image, the logo is shown - independently of the payment method. The payment method is not
     * lost by that: it stands in the header as its own tag, so a Selbstzahler whose bank is known is
     * labelled a Selbstzahler AND recognisable by its bank, instead of trading one for the other.
     *
     * WHY IT BEATS THE GENERIC ICON. {@link getPaymentMethodIcon} answers `pi pi-building` for every
     * Einzug, so an ING-DiBa and a Commerzbank account would get byte-identical headers. The logo is
     * the one thing that makes the account recognisable at a glance, and it is already on the loaded
     * object: the view requests the `currentBankAccountDto` relation node (the SEPA bank time slice,
     * bm.CustomerBillingAccountBank), so this costs no extra read.
     *
     * THE FALLBACK IS THE GENERIC PAYMENT-METHOD ICON: an empty token makes the header render
     * {@link getPaymentMethodIcon} instead, so an account without a bank image is never iconless.
     *
     * A SUSPENDED Einzug still shows its bank, because it still HAS one - the suspension is carried
     * by the header's own opacity, the same way it already was for the generic icon.
     *
     * The FontAwesome-vs-URL decision is NOT made here: `bm-bank-badge` owns it, together with the
     * initials fallback for a bank the directory never gave an icon.
     */
    getHeaderBankImage(): string {
        return this.customerBillingAccountDto?.currentBankAccountDto?.customerBankAccountDto?.bankImage ?? '';
    }

    /** The bank's name for the header avatar - the badge's initials fallback and its alt text. */
    getHeaderBankName(): string {
        return this.customerBillingAccountDto?.currentBankAccountDto?.customerBankAccountDto?.bankName ?? '';
    }

    /**
     * True when the bank account collected from belongs to someone OTHER than the customer - the
     * one header fact a clerk must not have to open a tab to find.
     */
    hasDeviantAccountHolder(): boolean {
        return this.customerBillingAccountDto?.currentBankAccountDto?.customerBankAccountDto?.deviantAccountHolder === true;
    }

    /**
     * Name of the deviant account holder: the linked person when the holder is kept as one, else the
     * free-text holder name of the bank account.
     */
    getDeviantAccountHolderName(): string {
        const bankAccount = this.customerBillingAccountDto?.currentBankAccountDto?.customerBankAccountDto;
        return bankAccount?.deviantAccountHolderPersonDtoName || bankAccount?.accountHolderName || '';
    }

    loadContractPriceTotals(): void {
        if (!this.customerBillingAccountDto?.contractPaymentAccounts?.length) {
            this.contractPriceTotalCurrent = null;
            this.contractPriceTotalFuture = null;
            return;
        }

        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const todayTime = now.getTime();
        const openEndTime = new Date('9999-12-31').getTime();

        let totalCurrent = 0;
        let totalFuture = 0;

        for (const cpa of this.customerBillingAccountDto.contractPaymentAccounts) {
            // Only the link row VALID TODAY for this account may count - startDate <= today <=
            // endDate, per contract. The business key of bm.ContractPaymentAccount is the contract
            // alone, so a contract with several link rows here carries the SAME contractDto.endDate
            // on every row — summing per row counted its premium once PER ROW — and a superseded row
            // can also belong to a contract that moved to ANOTHER billing account. Filtering by the
            // row's own endDate (the current row is high-dated 9999-12-31 and passes naturally; null
            // counts as current) handles both; a dedupe by contractDtoId would NOT — it would still
            // count contracts that genuinely left this account. Do not "simplify" this into a dedupe.
            //
            // PROVEN LIVE DEFECT (2026-09-26): a payment-FREQUENCY change writes a NEW link row with
            // a FUTURE startDate and high-dates its endDate to 9999-12-31, while the superseded OLD
            // row's endDate only moves to the day before the new row's start. Between the write and
            // that start date BOTH rows have an endDate >= today, so the old (endDate-only) filter
            // let BOTH count — account 401 / contract 51688 doubled 18,54 € to 37,08 €. The missing
            // half is the START bound: a row whose OWN startDate is still in the future has not begun
            // yet and must not contribute, regardless of its endDate.
            const linkStartTime = this.toLocalMidnightTime(cpa.startDate);
            if (linkStartTime != null && linkStartTime > todayTime) {
                continue;
            }
            const linkEndTime = this.toLocalMidnightTime(cpa.endDate);
            if (linkEndTime != null && linkEndTime < todayTime) {
                continue;
            }

            const price = cpa.contractDto?.contractPrice;
            const endDate = cpa.contractDto?.endDate;

            if (!price || !endDate) {
                continue;
            }

            const amount = price.paymentAmount ?? price.priceGross ?? 0;
            const endTime = new Date(endDate).getTime();

            // Aktuell: nur Verträge mit endDate >= TODAY
            if (endTime >= todayTime) {
                totalCurrent += amount;
            }

            // Zukunft: nur unbefristete Verträge (endDate >= 9999-12-31)
            if (endTime >= openEndTime) {
                totalFuture += amount;
            }
        }

        this.contractPriceTotalCurrent = totalCurrent;
        this.contractPriceTotalFuture = totalFuture;
    }

    /**
     * Returns severity for balance display.
     */
    getBalanceSeverity(balance: number): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (balance === 0) {
            return 'success';
        } else if (balance > 0) {
            return 'info';
        } else {
            return 'danger';
        }
    }

    /**
     * Severity of the SEPA status, from `sepaFailureState`.
     *
     * - `secondary` while the state of the displayed account is unknown — its read is still running
     *   or has failed. The side view renders its neutral placeholder tag with it; an unknown state must
     *   never be shown as `success` ("keine Fehlschläge").
     * - `success` for no active failure, `warn` for 1-2, `danger` from 3 consecutive failures on.
     *
     * The thresholds are a display heuristic, NOT the retry budget: that is configured per return
     * reason (`bm.SepaDirectDebitReturnReason.retryCount`), which this view does not read. The
     * reconciliation panel reads it and shows the real "Fehlversuche / max." ratio.
     */
    getSepaSeverity(): 'success' | 'warn' | 'danger' | 'secondary' {
        if (!this.sepaFailureState) {
            return 'secondary';
        }
        const failCount = this.sepaFailureState.failCount;
        if (failCount >= 3) {
            return 'danger';
        } else if (failCount > 0) {
            return 'warn';
        }
        return 'success';
    }

    /**
     * Returns severity for dunning status.
     */
    getDunningSeverity(): 'success' | 'warn' | 'danger' | 'secondary' {
        if (!this.runningDunning?.dunningId) {
            return 'success';
        }
        return 'danger';
    }

    /**
     * Returns account status label.
     */
    getAccountStatusLabel(): string {
        return this.customerBillingAccountDto?.closed ? 'Geschlossen' : 'Aktiv';
    }

    /**
     * Returns account status severity.
     */
    getAccountStatusSeverity(): 'success' | 'danger' {
        return this.customerBillingAccountDto?.closed ? 'danger' : 'success';
    }

    /**
     * Subscribe to entity change events for related entities.
     * Refreshes account data when transactions, balance, or the account itself changes.
     */
    private subscribeToObjectChanges(): void {
        this.objectChangeSubscription = this.objectService.objectChangeEvent.subscribe(change => {
            if (change.objectType === 'bm.CustomerBillingAccountTransaction'
                || change.objectType === 'bm.CustomerBillingAccount'
                || change.objectType === 'bm.CustomerBillingAccountBalance') {
                this.getCustomerBillingAccount();
            }
        });
    }

    /**
     * NO `super.ngOnDestroy()` HERE, AND THAT IS CORRECT: this component extends
     * {@link ObjectBaseModeComponent}, which deliberately defines no `ngOnDestroy` (see its own note,
     * TM-1000001554 - it binds its subscriptions' lifetime to the component through `DestroyRef`
     * exactly because subclasses override this hook). `ObjectBaseComponent#ngOnDestroy` belongs to a
     * DIFFERENT base class that this view does not inherit from; calling `super.ngOnDestroy()` would
     * not compile. The OBC checker's G4.4 assumes the other base class.
     */
    ngOnDestroy(): void {
        if (this.objectChangeSubscription) {
            this.objectChangeSubscription.unsubscribe();
        }
        if (this.sepaFailureStateSubscription) {
            this.sepaFailureStateSubscription.unsubscribe();
        }
        if (this.bankSlicesSubscription) {
            this.bankSlicesSubscription.unsubscribe();
        }
        this.runningDunningSubscription?.unsubscribe();
        this.openCwCaseLinesSubscription?.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.initialized) {
            return;
        }

        if (changes['objectIdentifier']) {
            this.getCustomerBillingAccount();
            this.resetDunningNavigation();
        }
    }

    // ========================================================================
    // Dunning Navigation Methods
    // ========================================================================

    /**
     * Opens the active dunning object within the component as a breadcrumb sub-view.
     */
    openDunningObject(): void {
        if (!this.runningDunning?.dunningId) {
            return;
        }

        const dunningId = this.runningDunning.dunningId;
        const dunningName = `Mahnung #${dunningId}` + (this.runningDunning.levelName ? ` (${this.runningDunning.levelName})` : '');

        this.dunningObjectIdentifier = new ObjectIdentifier('bd.Dunning', dunningId);
        this.showDunningObject = true;

        this.dunningBreadcrumb = [
            {
                label: this.customerBillingAccountDto.name || `Konto #${this.customerBillingAccountDto.id}`,
                command: () => this.backToAccountView()
            },
            {label: dunningName}
        ];
    }

    /**
     * Returns to the account view from the dunning sub-view.
     */
    backToAccountView(): void {
        this.resetDunningNavigation();
    }

    /**
     * Resets the dunning navigation state.
     */
    private resetDunningNavigation(): void {
        this.showDunningObject = false;
        this.dunningObjectIdentifier = null;
        this.dunningBreadcrumb = [];
    }

}
